import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSafeThemeClasses } from '@/shared/theme';
import { data } from '@/content/data';
import { SEO } from '@/shared/components/SEO';

const SNAPSHOT_ICON_PATHS: Record<string, string> = {
    bolt: 'M13 10V3L4 14h7v7l9-11h-7z',
    checkCircle: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    users:
        'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    lock: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
    globe:
        'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    heart:
        'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
};

function missionParagraphClassName(index: number, theme: ReturnType<typeof useSafeThemeClasses>): string {
    const base = `${theme.textSecondary} text-lg md:text-xl leading-relaxed font-light`;
    if (index === 0) return `${base} mb-8`;
    if (index >= 2) return `${base} mt-8`;
    return base;
}

export default function AboutPage() {
    const theme = useSafeThemeClasses();
    const [pageLoaded, setPageLoaded] = useState(false);
    const copy = data.about;

    useEffect(() => {
        const timer = setTimeout(() => {
            setPageLoaded(true);
        }, 200);

        return () => clearTimeout(timer);
    }, []);

    const containerVariants = {
        hidden: {
            opacity: 0,
            y: 30,
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                staggerChildren: 0.1,
                ease: [0.22, 1, 0.36, 1],
            },
        },
    };

    const itemVariants = {
        hidden: {
            opacity: 0,
            y: 20,
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1],
            },
        },
    };

    return (
        <div className={`min-h-screen ${theme.background}`}>
            <SEO title="About" path="/about" />
            <div className='absolute inset-0 overflow-hidden'>
                <div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[0]}`}></div>
                <div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[1]}`}></div>
            </div>

            <motion.div
                className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-28 pb-20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
            >
                <motion.div
                    className="text-center mb-20"
                    variants={containerVariants}
                    initial="hidden"
                    animate={pageLoaded ? 'visible' : 'hidden'}
                >
                    <motion.h1
                        className={`text-4xl md:text-6xl font-bold mb-6 ${theme.textHeading}`}
                        variants={itemVariants}
                    >
                        {copy.hero.title}
                    </motion.h1>
                    <motion.p
                        className={`text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed font-light ${theme.textSecondary}`}
                        variants={itemVariants}
                    >
                        {copy.hero.subtitle}
                    </motion.p>
                </motion.div>

                <motion.section
                    className={`mb-20 rounded-3xl p-8 md:p-12 ${theme.card}`}
                    variants={containerVariants}
                    initial="hidden"
                    animate={pageLoaded ? 'visible' : 'hidden'}
                >
                    <motion.h2
                        className={`text-3xl md:text-4xl font-semibold mb-8 text-center ${theme.textHeading}`}
                        variants={itemVariants}
                    >
                        {copy.mission.title}
                    </motion.h2>
                    {copy.mission.paragraphs.map((text, i) => (
                        <motion.p
                            key={`mission-p-${i}`}
                            className={missionParagraphClassName(i, theme)}
                            variants={itemVariants}
                        >
                            {text}
                        </motion.p>
                    ))}
                </motion.section>

                <motion.section
                    className="mb-20"
                    variants={containerVariants}
                    initial="hidden"
                    animate={pageLoaded ? 'visible' : 'hidden'}
                >
                    <motion.h2
                        className={`text-3xl md:text-4xl font-semibold mb-12 text-center ${theme.textHeading}`}
                        variants={itemVariants}
                    >
                        {copy.snapshot.title}
                    </motion.h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {copy.snapshot.items.map((item) => {
                            const pathD = SNAPSHOT_ICON_PATHS[item.icon] ?? SNAPSHOT_ICON_PATHS.bolt;
                            return (
                                <motion.div
                                    key={item.title}
                                    className={`p-8 rounded-2xl transition-all duration-300 hover:scale-105 ${theme.card}`}
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <div
                                        className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${theme.textAccent}`}
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d={pathD}
                                            />
                                        </svg>
                                    </div>
                                    <h3 className={`text-xl font-semibold mb-4 ${theme.textHeading}`}>{item.title}</h3>
                                    <p className={`leading-relaxed ${theme.textSecondary}`}>{item.body}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.section>

                <motion.section
                    className={`mb-20 rounded-3xl p-8 md:p-12 text-center ${theme.card}`}
                    variants={containerVariants}
                    initial="hidden"
                    animate={pageLoaded ? 'visible' : 'hidden'}
                >
                    <motion.h2
                        className={`text-3xl md:text-4xl font-semibold mb-8 ${theme.textHeading}`}
                        variants={itemVariants}
                    >
                        {copy.story.title}
                    </motion.h2>
                    <motion.p
                        className={`text-lg md:text-xl leading-relaxed max-w-3xl mx-auto font-light ${theme.textSecondary}`}
                        variants={itemVariants}
                    >
                        {copy.story.paragraph}
                    </motion.p>
                </motion.section>

                <motion.section
                    className={`text-center rounded-3xl p-8 md:p-12 ${theme.card}`}
                    variants={containerVariants}
                    initial="hidden"
                    animate={pageLoaded ? 'visible' : 'hidden'}
                >
                    <motion.h2
                        className={`text-2xl md:text-3xl font-semibold mb-6 ${theme.textHeading}`}
                        variants={itemVariants}
                    >
                        {copy.cta.title}
                    </motion.h2>
                    <motion.p
                        className={`text-lg mb-8 max-w-2xl mx-auto ${theme.textSecondary}`}
                        variants={itemVariants}
                    >
                        {copy.cta.paragraph}
                    </motion.p>
                    <motion.div variants={itemVariants}>
                        <motion.a
                            href={copy.cta.contactHref}
                            className={`inline-block px-8 py-4 rounded-xl transition-all duration-300 text-base font-semibold ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:shadow-xl`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {copy.cta.buttonLabel}
                        </motion.a>
                    </motion.div>
                </motion.section>
            </motion.div>
        </div>
    );
}
